import jsPDF from 'jspdf';
import { fabric } from 'fabric-with-gestures';
import opentype from 'opentype.js';
import colorImageUrl from '../image/colorUrl';
import { buildProof } from './proof';

// The shop's name, stamped over every download. A file that leaves the site is
// a preview of a design that has not been ordered yet, and this is what says so.
export const WATERMARK_TEXT = 'Shirt-discounter';

const CANVAS_WIDTH = 950;
const CANVAS_HEIGHT = 900;

// "ontwerpvoorbeeld-E150.pdf"; only letters, digits and dashes from the SKU, so
// a product name cannot put a path or odd characters in the file name.
export function proofFileName(sku) {
    const safe = String(sku || '').replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '');
    return safe ? `ontwerpvoorbeeld-${safe}.pdf` : 'ontwerpvoorbeeld.pdf';
}

export default class DownloadTools {
    // `watermark` is what tells a customer's preview apart from a file that is
    // going to be printed: the admin side pulls the artwork for production, and a
    // shop name across it there would end up on the shirt.
    constructor({ sides, SITE_URL, watermark = true }) {
        this.sides = sides || [];
        this.SITE_URL = SITE_URL;
        this.watermark = watermark;
    }

    // Which sides end up in the file. `allSides` is the whole product - front,
    // back and whatever else it prints on; without it the download is the one
    // side the customer is looking at, which is what the button next to the
    // preview means by "download".
    sidesToRender(allSides, currentSide) {
        if (allSides) return this.sides.slice();
        const side = currentSide || this.sides[0];
        return side ? [side] : [];
    }

    // The name written across the side, over the design rather than under it, so
    // it is part of the picture the file shows. Tiled at an angle instead of set
    // down once in the middle: a single mark sits in a known place and is one
    // crop away from gone.
    addWatermark(fabricCanvas) {
        if (!this.watermark) return;
        const stepX = 300;
        const stepY = 200;
        for (let y = -stepY; y < CANVAS_HEIGHT + stepY; y += stepY) {
            // Every other row starts half a step in, so the marks do not line up
            // into columns with clear lanes between them.
            const offset = Math.abs(Math.round(y / stepY)) % 2 ? stepX / 2 : 0;
            for (let x = -stepX; x < CANVAS_WIDTH + stepX; x += stepX) {
                fabricCanvas.add(new fabric.Text(WATERMARK_TEXT, {
                    left: x + offset,
                    top: y,
                    // Named rather than one of the shop's own faces: this is drawn
                    // in a throwaway canvas, and an SVG download carries the name
                    // out to whatever opens the file.
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    fontSize: 26,
                    fill: 'rgba(0, 0, 0, 0.16)',
                    angle: -30,
                    selectable: false,
                    evented: false
                }));
            }
        }
    }

    // Whether the customer put anything on a side. The proof shows a printed
    // side large and marks it; a side with nothing on it is only the shirt.
    isPrinted(side) {
        return (this.designs[side] || []).some(el => el.type === 'text' || el.type === 'image');
    }

    // Helper to render a side to a dataURL. `watermark` off is for the proof,
    // which lays its own watermark over the picture.
    renderSideToDataURL(side, format = 'png', watermark = true) {
		const self = this;
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = CANVAS_WIDTH;
            canvas.height = CANVAS_HEIGHT;
            // Retina scaling off: with it fabric resizes the element to the screen's
            // pixel ratio, so a phone or a scaled Windows display got a file whose
            // shirt was blown up and cropped to its top-left corner.
            const fabricCanvas = new fabric.Canvas(canvas, { enableRetinaScaling: false });
            //console.log(colors.find(c => c.color === selectedShirtColor)[side]);
            // Set background image
            fabric.Image.fromURL(colorImageUrl(self.SITE_URL, self.colors.find(c => c.color === self.selectedShirtColor)[side]), (img) => {
                //console.log(img);
                fabricCanvas.setBackgroundImage(img, () => {
                    // Add design elements
                    (self.designs[side] || []).forEach(el => {
                        if (el.type === 'text') {
                            const text = new fabric.Text(el.text, el);
                            fabricCanvas.add(text);
                        } else if (el.type === 'image') {
                            fabric.Image.fromURL(el.src, imgObj => {
                                imgObj.set(el);
                                fabricCanvas.add(imgObj);
                            }, { crossOrigin: 'anonymous' });
                        }
                    });
                    setTimeout(() => {
                        // Last, in here rather than after the loop above: the
                        // images arrive from their own callbacks and would land
                        // on top of a watermark added before them.
                        if (watermark) self.addWatermark(fabricCanvas);
                        resolve(fabricCanvas.toDataURL(`image/${format}`));
                        fabricCanvas.dispose();
                    }, 500);
                }, {
                    scaleX: CANVAS_WIDTH / img.width,
                    scaleY: CANVAS_HEIGHT / img.height
                });
            }, { crossOrigin: 'anonymous' });
        });
    }

    // Helper to render a side to SVG
    renderSideToSVG(side) {
		const self = this;
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = CANVAS_WIDTH;
            canvas.height = CANVAS_HEIGHT;
            const fabricCanvas = new fabric.Canvas(canvas, { enableRetinaScaling: false });
            fabric.Image.fromURL(colorImageUrl(self.SITE_URL, self.colors.find(c => c.color === self.selectedShirtColor)[side]), (img) => {
                fabricCanvas.setBackgroundImage(img, () => {
                    (self.designs[side] || []).forEach(async el => {
                        if (el.type === 'text') {
							//console.log(el);
							//console.log('Loading font for SVG text rendering...');
                            const font = await opentype.load(`/fonts/${el.fontFamily}.ttf`);
							
							const path = font.getPath(el.text, 0, 0, el.fontSize || 40);
							//console.log('Generated path for text:', path);
							const svgPath = path.toPathData();
							//console.log('SVG path data for text:', svgPath);
							const vector = new fabric.Path(svgPath, {
								fill: el.fill,
								left: el.left || 0,
								top: el.top || 0,
							});
							fabricCanvas.add(vector);
                        } else if (el.type === 'image') {
                            fabric.Image.fromURL(el.src, imgObj => {
                                imgObj.set(el);
                                fabricCanvas.add(imgObj);
                            }, { crossOrigin: 'anonymous' });
                        }
                    });
                    setTimeout(() => {
                        self.addWatermark(fabricCanvas);
                        resolve(fabricCanvas.toSVG());
                        fabricCanvas.dispose();
                    }, 500);
                }, {
                    scaleX: CANVAS_WIDTH / img.width,
                    scaleY: CANVAS_HEIGHT / img.height
                });
            }, { crossOrigin: 'anonymous' });
        });
    }

    // Download as PNG/JPG
    async handleDownloadImage(format, sides){
		const self = this;
        // Render the sides asked for, stack vertically
        //console.log(colors);
        const dataURLs = await Promise.all(sides.map(side => self.renderSideToDataURL(side, format)));
        //console.log(dataURLs);
        const totalHeight = CANVAS_HEIGHT * sides.length;
        const canvas = document.createElement('canvas');
        canvas.width = CANVAS_WIDTH;
        canvas.height = totalHeight;
        const ctx = canvas.getContext('2d');
        for (let i = 0;i < dataURLs.length;i++) {
            const img = new window.Image();
            img.src = dataURLs[i];
            await new Promise(res => { img.onload = res; });
            ctx.drawImage(img, 0, i * CANVAS_HEIGHT);
        }
        const link = document.createElement('a');
        link.download = `design.${format}`;
        link.href = canvas.toDataURL(`image/${format}`);
        link.click();
    }

    // Download as SVG
    async handleDownloadSVG(sides){
		const self = this;
        const svgs = await Promise.all(sides.map(side => self.renderSideToSVG(side)));
        // Stack SVGs vertically by wrapping in a single SVG
        const width = CANVAS_WIDTH;
        const height = CANVAS_HEIGHT * sides.length;
        let y = 0;
        let content = '';
        for (let svg of svgs) {
            // Remove outer <svg ...>...</svg>
            const inner = svg.replace(/^[\s\S]*?<svg[^>]*>|<\/svg>[\s\S]*$/g, '');
            content += `<g transform="translate(0,${y})">${inner}</g>`;
            y += CANVAS_HEIGHT;
        }
        const finalSVG = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${width}" height="${height}">${content}</svg>`;
        const blob = new Blob([finalSVG], { type: 'image/svg+xml' });
        const link = document.createElement('a');
        link.download = 'design.svg';
        link.href = URL.createObjectURL(blob);
        link.click();
    }

    // Download as PDF. For the customer that is the design proof in the shop's
    // house style (./proof.js). The admin, who downloads without a watermark,
    // gets the bare artwork a page per side: that file goes to production, and a
    // "VOORBEELD" page would be the wrong thing to print from.
    async handleDownloadPDF(sides, details = {}){
        if (this.watermark) {
            try {
                await this.handleDownloadProof(details);
                return;
            } catch (err) {
                // Fonts or logo did not load: better the plain pages than nothing.
                console.error('DownloadTools: proof failed, falling back to plain PDF', err);
            }
        }
		const self = this;
        const dataURLs = await Promise.all(sides.map(side => self.renderSideToDataURL(side, 'jpeg')));
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'px', format: [CANVAS_WIDTH, CANVAS_HEIGHT] });
        for (let i = 0;i < dataURLs.length;i++) {
            if (i > 0) pdf.addPage([CANVAS_WIDTH, CANVAS_HEIGHT]);
            pdf.addImage(dataURLs[i], 'JPEG', 0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
        }
        pdf.save('design.pdf');
    };

    // The proof always covers the whole product, whatever side is on screen:
    // it is one page that shows the printed side large and the rest beside it.
    async handleDownloadProof(details) {
        const sides = this.sidesToRender(true);
        const images = await Promise.all(sides.map(side => this.renderSideToDataURL(side, 'png', false)));
        const sku = String(details.sku || '').toUpperCase();
        const pdf = await buildProof(
            sides.map((side, i) => ({ side, image: images[i], printed: this.isPrinted(side) })),
            { sku, color: this.selectedShirtColor, printMethod: details.printMethod }
        );
        pdf.save(proofFileName(sku));
    }

	// `currentSide` is what a download without allSides is of - the side on screen.
	// Left out, it falls back to the first side the product has, so a caller that
	// does not track a side still gets a file rather than nothing.
	// `details` fills the proof's cards: { sku, printMethod }. The colour comes
	// from selectedShirtColor.
	downloadDesign(withProduct = true, format = 'png', allSides = false, designs, selectedShirtColor, colors, currentSide, details = {}) {
		this.designs = designs.current || {};
		this.selectedShirtColor = selectedShirtColor.current || '';
		this.colors = colors || [];

		const sides = this.sidesToRender(allSides, currentSide);
		if (sides.length === 0) {
			console.warn('DownloadTools: no side to download');
			return;
		}

		if (format === 'pdf') {
			this.handleDownloadPDF(sides, details);
		} else if (format === 'svg') {
			this.handleDownloadSVG(sides);
		} else {
			this.handleDownloadImage(format, sides);
		}
	}
																																				
}
